import { ExternalLink, BookOpen, FileText, Globe, Video, GraduationCap, BarChart2 } from "lucide-react";
import { useNodeSources, Source } from "@/hooks/useNodeSources";

const TYPE_ICON: Record<string, React.ReactNode> = {
  book: <BookOpen className="h-4 w-4" />,
  article: <FileText className="h-4 w-4" />,
  website: <Globe className="h-4 w-4" />,
  video: <Video className="h-4 w-4" />,
  course: <GraduationCap className="h-4 w-4" />,
  report: <BarChart2 className="h-4 w-4" />,
};

const TYPE_LABEL: Record<string, string> = {
  book: "Book",
  article: "Article",
  website: "Website",
  video: "Video",
  course: "Course",
  report: "Report",
};

function SourceCard({ source }: { source: Source }) {
  const icon = TYPE_ICON[source.type] ?? <FileText className="h-4 w-4" />;
  const label = TYPE_LABEL[source.type] ?? source.type;

  const inner = (
    <div className="group flex gap-3 rounded-lg border bg-card p-4 transition-colors hover:border-primary/40 hover:bg-muted/40">
      <div className="mt-0.5 shrink-0 text-muted-foreground group-hover:text-primary transition-colors">
        {icon}
      </div>
      <div className="min-w-0 flex-1 space-y-1">
        <div className="flex items-start justify-between gap-2">
          <p className="text-sm font-medium leading-snug text-foreground group-hover:text-primary transition-colors line-clamp-2">
            {source.title}
          </p>
          {source.url && (
            <ExternalLink className="h-3.5 w-3.5 shrink-0 mt-0.5 text-muted-foreground group-hover:text-primary transition-colors" />
          )}
        </div>
        {source.author && (
          <p className="text-xs text-muted-foreground">{source.author} &middot; <span className="capitalize">{label}</span></p>
        )}
        <p className="text-xs text-muted-foreground/80 leading-relaxed">{source.description}</p>
      </div>
    </div>
  );

  if (source.url) {
    return (
      <a href={source.url} target="_blank" rel="noopener noreferrer" className="block no-underline">
        {inner}
      </a>
    );
  }

  return inner;
}

interface FurtherReadingProps {
  nodeId: string;
  nodeName: string;
  courseId?: string;
}

export default function FurtherReading({ nodeId, nodeName, courseId }: FurtherReadingProps) {
  const { sources, loading } = useNodeSources(nodeId, nodeName, { courseId });

  if (loading) {
    return (
      <div className="mt-16 space-y-3">
        <div className="h-4 w-40 rounded bg-muted animate-pulse" />
        <div className="grid gap-3 sm:grid-cols-2">
          {[1, 2, 3, 4].map((i) => (
            <div key={i} className="h-24 rounded-lg border bg-muted animate-pulse" />
          ))}
        </div>
      </div>
    );
  }

  if (!sources.length) return null;

  return (
    <section className="mt-16">
      <div className="mb-4">
        <h3 className="text-base font-semibold">Further Reading</h3>
        <p className="text-sm text-muted-foreground mt-0.5">
          Explore these resources to deepen your understanding of this topic.
        </p>
      </div>
      <div className="grid gap-3 sm:grid-cols-2">
        {sources.map((source, i) => (
          <SourceCard key={i} source={source} />
        ))}
      </div>
    </section>
  );
}
